import React, { useState, useEffect } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import Breadcrumbs from "../../core/breadcrumbs";
import { getProfitLossReport } from "../../service/ApiServices";
import moment from 'moment';
import { Spin } from 'antd';
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const ProfitLoss = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(moment().subtract(30, 'days'));
  const [endDate, setEndDate] = useState(moment());

  const fetchReport = async (start, end) => {
    try {
      setLoading(true);
      const response = await getProfitLossReport(
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD')
      );
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(startDate, endDate);
  }, []);

  const handleCallback = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleApplyDate = () => {
    fetchReport(startDate, endDate);
  };

  const downloadPDF = () => {
    try {
      const customWidth = 210; 
      const customHeight = 297; 

      const doc = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: [customWidth, customHeight],
      });
      const pageWidth = doc.internal.pageSize.getWidth();

      
      doc.setFontSize(20);
      doc.text("The Suit - Profit & Loss Report", pageWidth / 2, 20, { align: "center" });

      
      doc.setFontSize(12);
      doc.text(`Period: ${startDate.format('YYYY-MM-DD')} to ${endDate.format('YYYY-MM-DD')}`, pageWidth / 2, 30, { align: "center" });

     
      doc.setDrawColor(150, 150, 181);
      doc.line(10, 35, pageWidth - 10, 35);

      if (reportData) {
        const tableData = [
          
          ["Income", ""],
          ["Paid Invoices", `$${reportData.income.paidInvoices.toFixed(2)}`],
          ["Partially Paid Invoices", `$${reportData.income.partialPaidInvoices.toFixed(2)}`],
          ["Cancelled Invoices", `-$${reportData.income.cancelledInvoices.toFixed(2)}`],
          ["Total Income", `$${reportData.income.totalIncome.toFixed(2)}`],
          ["Gross Profit", `$${reportData.grossProfit.toFixed(2)}`],
          
          
          ["Expenses", ""],
          ...Object.entries(reportData.expenses.byCategory).map(([category, amount]) => 
            [category, `$${amount.toFixed(2)}`]
          ),
          ["Total Expenses", `$${reportData.expenses.total.toFixed(2)}`],
          
          
          ["Net Profit", `$${reportData.netProfit.toFixed(2)}`]
        ];

        doc.autoTable({
          startY: 40,
          head: [["Category", "Amount"]],
          body: tableData,
          theme: "grid",
          styles: { 
            fillColor: [255, 255, 255], 
            textColor: [0, 0, 0],
            fontSize: 10
          },
          headStyles: { 
            fillColor: [74, 74, 73], 
            textColor: [255, 255, 255],
            fontSize: 12
          },
          columnStyles: {
            0: { cellWidth: 100 },
            1: { cellWidth: 60, halign: 'right' }
          },
          margin: { left: 25 }
        });
      }

      
      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const view = window.open();
      view.document.write(`<iframe width="100%" height="100%" src="${pdfUrl}"></iframe>`);
      view.document.close();

    } catch (error) {
      console.error("Error in downloadPDF function:", error);
    }
  };

  const downloadExcel = () => {
    if (!reportData) return;

    const worksheetData = [
      ["The Suit - Profit & Loss Report"],
      [`Period: ${startDate.format('YYYY-MM-DD')} to ${endDate.format('YYYY-MM-DD')}`],
      [],
      ["Category", "Amount"],
      
      ["Income", ""],
      ["Paid Invoices", reportData.income.paidInvoices.toFixed(2)],
      ["Partially Paid Invoices", reportData.income.partialPaidInvoices.toFixed(2)],
      ["Cancelled Invoices", `-${reportData.income.cancelledInvoices.toFixed(2)}`],
      ["Total Income", reportData.income.totalIncome.toFixed(2)],
      ["Gross Profit", reportData.grossProfit.toFixed(2)],
      
      ["Expenses", ""],
      ...Object.entries(reportData.expenses.byCategory).map(([category, amount]) => 
        [category, amount.toFixed(2)]
      ),
      ["Total Expenses", reportData.expenses.total.toFixed(2)],
      
      ["Net Profit", reportData.netProfit.toFixed(2)]
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Profit & Loss Report");
    XLSX.writeFile(workbook, "Profit_Loss_Report.xlsx");
  };

  const handleRefresh = () => {
    fetchReport(startDate, endDate);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <Breadcrumbs
          maintitle="Profit & Loss Report"
          subtitle="Manage Your Profit & Loss Report"
          downloadPDF={downloadPDF}
          downloadExcel={downloadExcel}
          onRefresh={handleRefresh}
        />
        <div className="card table-list-card border-0 mb-0">
          <div className="card-body mb-3">
            <div className="table-top mb-0 profit-table-top">
              <div className="position-relative daterange-wraper input-blocks mb-0" style={{ width: '200px' }}>
                <DateRangePicker
                  initialSettings={{
                    startDate: startDate.toDate(),
                    endDate: endDate.toDate(),
                    ranges: {
                      'Today': [moment(), moment()],
                      'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                      'Last 7 Days': [moment().subtract(6, 'days'), moment()],
                      'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                      'This Month': [moment().startOf('month'), moment().endOf('month')],
                      'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
                    }
                  }}
                  onCallback={handleCallback}
                >
                  <input
                    className="form-control col-4 input-range"
                    type="text"
                    value={`${startDate.format('YYYY-MM-DD')} - ${endDate.format('YYYY-MM-DD')}`}
                    readOnly
                  />
                </DateRangePicker>
              </div>
              <div className="date-btn">
                <button
                  onClick={handleApplyDate}
                  className="btn btn-secondary d-flex align-items-center"
                >
                  <i className="feather-14 info-img me-2" />
                  Display Report
                </button>
              </div>
            </div>
          </div>
        </div>

        <Spin spinning={loading}>
          <div className="table-responsive">
            {reportData && (
              <table className="table profit-table">
                <thead className="profit-table-bg">
                  <tr>
                    <th>Category</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="table-heading bg-info">
                    <td>Income</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>Paid Invoices</td>
                    <td>Lkr.{reportData.income.paidInvoices.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Partially Paid Invoices</td>
                    <td>Lkr.{reportData.income.partialPaidInvoices.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Cancelled Invoices (Deduction)</td>
                    <td>-Lkr.{reportData.income.cancelledInvoices.toFixed(2)}</td>
                  </tr>
                  <tr className="profit-table-bg">
                    <td>Total Income</td>
                    <td>Lkr.{reportData.income.totalIncome.toFixed(2)}</td>
                  </tr>
                  <tr className="profit-table-bg">
                    <td>Gross Profit</td>
                    <td>Lkr.{reportData.grossProfit.toFixed(2)}</td>
                  </tr>
                  <tr className="table-heading bg-info">
                    <td>Expenses</td>
                    <td></td>
                  </tr>
                  {Object.entries(reportData.expenses.byCategory).map(([category, amount]) => (
                    <tr key={category}>
                      <td>{category}</td>
                      <td>Lkr.{amount.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="profit-table-bg">
                    <td>Total Expenses</td>
                    <td>Lkr.{reportData.expenses.total.toFixed(2)}</td>
                  </tr>
                  <tr className="profit-table-bg">
                    <td>Net Profit</td>
                    <td>Lkr.{reportData.netProfit.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default ProfitLoss;
